import { useEffect, useState } from "react";
import { toast } from "sonner";
import client from "../lib/api";
import {
  UsersThree, PaperPlaneTilt, Copy, X, Warning, CheckCircle,
  Trash, CaretDown, Sparkle, ShieldCheck, Handshake,
} from "@phosphor-icons/react";

/*
  TeamPage — Multi-user + role management for the account.
  Route: /app/team

  - Owner + Admin can invite / remove / change roles
  - Dispatcher / Ops Manager / Accounting / Viewer can view the roster (read-only)
  - Invite creates a pending membership + returns an invite link (copy-paste-able)
  - Invited user signs up with the invited email and clicks the link → accepts
*/

const ROLES = [
  { value: "admin", label: "Admin", desc: "Full access + can manage team + billing" },
  { value: "dispatcher", label: "Dispatcher", desc: "Manage loads, drivers, dispatch operations" },
  { value: "ops_manager", label: "Ops Manager", desc: "Dispatcher access + settings + analytics" },
  { value: "accounting", label: "Accounting", desc: "Invoices, payroll, reports (read + create)" },
  { value: "viewer", label: "Viewer", desc: "Read-only access to everything" },
];

const ROLE_BADGE_COLOR = {
  owner: "bg-[#FF5A1F]/15 text-[#FF9764] border-[#FF5A1F]/40",
  admin: "bg-purple-500/15 text-purple-300 border-purple-500/40",
  dispatcher: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  ops_manager: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  accounting: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  viewer: "bg-neutral-500/15 text-neutral-300 border-neutral-500/40",
};

export default function TeamPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  const load = async () => {
    try {
      const { data } = await client.get("/account/members");
      setData(data);
    } catch (e) {
      toast.error("Failed to load team members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onInvited = (result) => {
    setLastInvite(result);
    setInviteOpen(false);
    load();
  };

  if (loading) {
    return <div className="p-6 text-neutral-500 text-sm" data-testid="team-loading">Loading team…</div>;
  }

  const canManage = data?.can_manage;

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row">
        <div>
          <div className="overline mb-1">TEAM</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Your team</h1>
          <p className="text-neutral-500 text-sm mt-1">
            Invite dispatchers, ops managers, and accounting to your account with role-based permissions.
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setInviteOpen(true)}
            data-testid="team-invite-btn"
            className="btn-primary rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2 whitespace-nowrap"
          >
            <PaperPlaneTilt size={14} weight="bold" />
            Invite member
          </button>
        )}
      </div>

      {/* Last invite banner — shows the copyable link */}
      {lastInvite && (
        <InviteLinkBanner invite={lastInvite} onDismiss={() => setLastInvite(null)} />
      )}

      {/* Owner row */}
      <MemberSection title="Account Owner" testid="team-owner-section">
        <MemberRow member={data.owner} canManage={false} onChange={load} />
      </MemberSection>

      {/* Active members */}
      <MemberSection
        title={`Active Members (${data.active.length})`}
        testid="team-active-section"
        empty={data.active.length === 0 && "No team members yet. Invite your first dispatcher above."}
      >
        {data.active.map((m) => (
          <MemberRow key={m.id} member={m} canManage={canManage} onChange={load} />
        ))}
      </MemberSection>

      {/* Pending invites */}
      {data.pending.length > 0 && (
        <MemberSection title={`Pending Invites (${data.pending.length})`} testid="team-pending-section">
          {data.pending.map((m) => (
            <PendingRow key={m.id} invite={m} canManage={canManage} onChange={load} />
          ))}
        </MemberSection>
      )}

      {/* Role legend */}
      <RoleLegend />

      {/* Invite modal */}
      {inviteOpen && (
        <InviteModal onClose={() => setInviteOpen(false)} onInvited={onInvited} />
      )}
    </div>
  );
}

/* ─── SECTIONS ─── */
function MemberSection({ title, children, testid, empty }) {
  return (
    <div className="card-surface p-5 sm:p-6" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold mb-4">{title}</div>
      {empty ? (
        <div className="text-sm text-neutral-500 italic py-6 text-center" data-testid={`${testid}-empty`}>{empty}</div>
      ) : (
        <div className="divide-y divide-white/5">{children}</div>
      )}
    </div>
  );
}

function MemberRow({ member, canManage, onChange }) {
  const [changing, setChanging] = useState(false);
  const [removing, setRemoving] = useState(false);
  const isOwner = member.role === "owner";

  const changeRole = async (newRole) => {
    if (newRole === member.role) return;
    setChanging(true);
    try {
      await client.patch(`/account/members/${member.id}`, { role: newRole });
      toast.success(`Role updated to ${newRole.replace("_", " ")}`);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update role");
    } finally {
      setChanging(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${member.email || member.invited_email} from the team?`)) return;
    setRemoving(true);
    try {
      await client.delete(`/account/members/${member.id}`);
      toast.success("Member removed");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove member");
    } finally {
      setRemoving(false);
    }
  };

  const initials = (member.full_name || member.email || "?")
    .split(" ")
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();

  return (
    <div className="flex items-center gap-3 sm:gap-4 py-3" data-testid={`team-member-${member.id}`}>
      <div className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-xs font-semibold text-neutral-300 shrink-0">
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-white truncate">
          {member.full_name || member.email || member.invited_email}
        </div>
        <div className="text-xs text-neutral-500 truncate">{member.email || member.invited_email}</div>
      </div>
      <div className={`text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full border shrink-0 ${ROLE_BADGE_COLOR[member.role] || ROLE_BADGE_COLOR.viewer}`}>
        {member.role_label}
      </div>
      {canManage && !isOwner && (
        <div className="flex items-center gap-1 shrink-0">
          <RoleDropdown currentRole={member.role} onChange={changeRole} disabled={changing} />
          <button
            onClick={remove}
            disabled={removing}
            data-testid={`remove-member-${member.id}`}
            className="text-neutral-500 hover:text-red-400 transition-colors p-1.5 rounded disabled:opacity-40"
            title="Remove member"
          >
            <Trash size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

function PendingRow({ invite, canManage, onChange }) {
  const [canceling, setCanceling] = useState(false);
  const [resending, setResending] = useState(false);

  const cancel = async () => {
    if (!window.confirm(`Cancel the invite to ${invite.invited_email}?`)) return;
    setCanceling(true);
    try {
      await client.delete(`/account/invitations/${invite.id}`);
      toast.success("Invite canceled");
      onChange?.();
    } catch (e) {
      toast.error("Failed to cancel invite");
    } finally {
      setCanceling(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      const { data } = await client.post(`/account/invitations/${invite.id}/resend`);
      if (data.email_sent) {
        toast.success(`Invite resent to ${invite.invited_email}`);
      } else {
        toast.info(`Resend logged — email delivery isn't configured (${data.email_reason || "no reason"})`);
      }
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resend invite");
    } finally {
      setResending(false);
    }
  };

  const lastSentText = invite.last_sent_at
    ? `Resent ${_relTime(invite.last_sent_at)}`
    : `Invited ${new Date(invite.invited_at).toLocaleDateString()}`;

  return (
    <div className="flex items-center gap-3 sm:gap-4 py-3" data-testid={`team-pending-${invite.id}`}>
      <div className="w-9 h-9 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
        <PaperPlaneTilt size={14} className="text-amber-400" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-white truncate">{invite.invited_email}</div>
        <div className="text-xs text-neutral-500">Pending · {lastSentText}</div>
      </div>
      <div className={`text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full border shrink-0 ${ROLE_BADGE_COLOR[invite.role] || ROLE_BADGE_COLOR.viewer}`}>
        {invite.role_label}
      </div>
      {canManage && (
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={resend}
            disabled={resending}
            data-testid={`resend-invite-${invite.id}`}
            className="text-xs text-neutral-400 hover:text-[#FF9764] transition-colors px-2 py-1 rounded border border-white/10 hover:border-[#FF5A1F]/40 inline-flex items-center gap-1 disabled:opacity-40"
            title="Resend invite email"
          >
            <PaperPlaneTilt size={11} weight="bold" />
            {resending ? "Sending…" : "Resend"}
          </button>
          <button
            onClick={cancel}
            disabled={canceling}
            data-testid={`cancel-invite-${invite.id}`}
            className="text-neutral-500 hover:text-red-400 transition-colors p-1.5 rounded disabled:opacity-40"
            title="Cancel invite"
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

function _relTime(iso) {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const secs = Math.floor((now - then) / 1000);
    if (secs < 60) return "just now";
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
  } catch { return "recently"; }
}

/* ─── ROLE DROPDOWN ─── */
function RoleDropdown({ currentRole, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        disabled={disabled}
        data-testid="role-dropdown-btn"
        className="text-xs text-neutral-400 hover:text-white transition-colors px-2 py-1 rounded border border-white/10 hover:border-white/20 inline-flex items-center gap-1 disabled:opacity-40"
      >
        Change <CaretDown size={10} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-40 w-56 bg-[#141414] border border-white/10 rounded-lg shadow-xl overflow-hidden">
            {ROLES.map((r) => (
              <button
                key={r.value}
                onClick={() => { setOpen(false); onChange(r.value); }}
                data-testid={`role-option-${r.value}`}
                className={`w-full text-left px-3 py-2.5 text-xs hover:bg-white/5 transition-colors ${currentRole === r.value ? "bg-[#FF5A1F]/10" : ""}`}
              >
                <div className="font-medium text-white">{r.label}</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">{r.desc}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ─── INVITE MODAL ─── */
function InviteModal({ onClose, onInvited }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("dispatcher");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await client.post("/account/invitations", { email, role });
      if (data.email_sent) {
        toast.success(`Invitation emailed to ${email}`);
      } else {
        toast.info(`Invite created for ${email} — copy the link below to share`);
      }
      onInvited(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send invite");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="invite-modal-backdrop"
    >
      <div
        className="bg-[#0A0A0A] border border-white/10 rounded-xl max-w-md w-full p-6 sm:p-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="invite-modal"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="overline mb-1">INVITE MEMBER</div>
            <h2 className="font-display text-xl font-semibold">Add someone to the team</h2>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white p-1" data-testid="invite-modal-close">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <div className="overline mb-2">EMAIL</div>
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="invite-email-input"
              placeholder="dispatcher@yourfleet.com"
              className="w-full bg-[#141414] border border-white/10 rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#FF5A1F]/60"
            />
          </label>
          <label className="block">
            <div className="overline mb-2">ROLE</div>
            <div className="space-y-2">
              {ROLES.map((r) => (
                <label
                  key={r.value}
                  data-testid={`invite-role-${r.value}`}
                  className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${role === r.value ? "border-[#FF5A1F]/40 bg-[#FF5A1F]/5" : "border-white/10 hover:border-white/20"}`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={r.value}
                    checked={role === r.value}
                    onChange={(e) => setRole(e.target.value)}
                    className="mt-1 accent-[#FF5A1F]"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-white">{r.label}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">{r.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </label>
          <button
            type="submit"
            disabled={submitting}
            data-testid="invite-submit-btn"
            className="w-full btn-primary rounded-full py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {submitting ? "Creating invite…" : "Send invite"}
            <PaperPlaneTilt size={14} weight="bold" />
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─── INVITE LINK BANNER ─── */
function InviteLinkBanner({ invite, onDismiss }) {
  const [copied, setCopied] = useState(false);
  const inviteUrl = `${window.location.origin}/accept-invite/${invite.invite_token}`;
  const emailed = Boolean(invite.email_sent);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Could not copy. Select and copy manually.");
    }
  };

  return (
    <div className="rounded-lg border border-[#FF5A1F]/30 bg-[#FF5A1F]/5 p-4 sm:p-5" data-testid="invite-link-banner">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-[#FF5A1F]/20 flex items-center justify-center shrink-0">
          {emailed ? (
            <CheckCircle size={14} weight="fill" className="text-[#FF5A1F]" />
          ) : (
            <Sparkle size={14} weight="fill" className="text-[#FF5A1F]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white mb-1" data-testid="invite-banner-status">
            {emailed
              ? `Invitation emailed to ${invite.invited_email}`
              : `Invite created for ${invite.invited_email}`}
          </div>
          <div className="text-xs text-neutral-400 mb-3">
            {emailed
              ? "They'll receive a branded email with the accept link. You can also share this link directly if needed:"
              : "Email delivery isn't configured yet — share this invite link with them out-of-band (Slack, SMS, etc.). They'll need to sign up with the invited email address to accept."}
          </div>
          <div className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2">
            <div className="text-xs text-neutral-300 truncate flex-1 font-mono" data-testid="invite-link-url">{inviteUrl}</div>
            <button
              onClick={copy}
              data-testid="invite-link-copy"
              className="text-xs text-[#FF5A1F] hover:text-[#FF7038] inline-flex items-center gap-1 shrink-0"
            >
              {copied ? <><CheckCircle size={12} weight="fill" /> Copied</> : <><Copy size={12} /> Copy</>}
            </button>
          </div>
        </div>
        <button onClick={onDismiss} className="text-neutral-500 hover:text-white p-1" data-testid="invite-link-dismiss">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

/* ─── ROLE LEGEND ─── */
function RoleLegend() {
  return (
    <div className="card-surface p-5 sm:p-6" data-testid="role-legend">
      <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold mb-4 flex items-center gap-2">
        <ShieldCheck size={12} /> ROLE PERMISSIONS
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
        <LegendRow role="owner" label="Owner" desc="Account creator. Full access + billing + can't be removed." />
        <LegendRow role="admin" label="Admin" desc="Everything except closing the account or transferring ownership." />
        <LegendRow role="dispatcher" label="Dispatcher" desc="Loads, drivers, dispatch communications." />
        <LegendRow role="ops_manager" label="Ops Manager" desc="Dispatcher + settings + analytics + reports." />
        <LegendRow role="accounting" label="Accounting" desc="Invoices, payroll, reports (view + create)." />
        <LegendRow role="viewer" label="Viewer" desc="Read-only across the app. No mutations." />
      </div>
    </div>
  );
}

function LegendRow({ role, label, desc }) {
  return (
    <div className="flex items-start gap-2">
      <div className={`text-[9px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full border shrink-0 mt-0.5 ${ROLE_BADGE_COLOR[role]}`}>
        {label}
      </div>
      <div className="text-neutral-400 leading-relaxed">{desc}</div>
    </div>
  );
}
